/**
 * "Save Cart for Later" — TEAM-15276.
 *
 * The cart lives in sessionStorage (`koalaOrder`, see `sessionStorageBasket`), which a browser
 * scopes to one tab, so a diner who leaves the Toast repeat-failure modal by closing the tab came
 * back to an empty cart. Pressing the button copies the cart to localStorage (the fulfilment
 * included, so a delivery cart's address too), and the next page load in a tab that has never held
 * a cart — a new tab — restores it once and deletes the copy (`restoreSavedCartIntoSession`).
 *
 * What is deliberately NOT kept:
 * - the guest's name, email and phone. localStorage outlives the tab and guests never log out, so on
 *   a shared or kiosk device the next diner would get them pre-filled; the guest re-enters them
 *   instead (product decision, Kelcie Geark on TEAM-15276, 2026-10-07).
 * - `checkoutBasket` — the server basket. It carries the Toast session secret (`payment_meta`) and
 *   any gift card number, and it is the basket whose payment just failed twice. Without it,
 *   checkout creates a fresh basket from the saved items (`initializeOrderSaga`), as it does for
 *   any cart that has not reached checkout yet. Promos and rewards applied to the old one are not
 *   carried over.
 * - anything older than {@link SAVED_CART_TTL_MS} — the same two hours the session basket itself
 *   lasts. localStorage has no expiry of its own, so an expired copy is never restored and is
 *   deleted by the first page load that reads it, in any tab — including one that has a cart of
 *   its own. Until the app is loaded again it stays in the browser.
 *
 * The copy is also cleared once an order is placed and on logout (`clearSavedCart`).
 */
import * as Sentry from '@sentry/nextjs';
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from '@/constants/storageKeys';
import { type IBasketState } from '@/types/basket';
import { SESSION_BASKET_TTL_MS, sessionStorageBasket } from '@/utils/global';
import { isStorageFallbackActive } from '@/utils/storageFallback';

export const SAVED_CART_TTL_MS = SESSION_BASKET_TTL_MS;

/** The session basket minus the server basket and any submitted order — see the module comment. */
type SavedBasket = Omit<IBasketState, 'checkoutBasket' | 'checkoutOrder'> & {
  checkoutBasket: null;
  checkoutOrder: null;
};

interface SavedCart {
  savedAt: number;
  basket: SavedBasket;
}

const breadcrumb = (message: string, data?: Record<string, unknown>) =>
  Sentry.addBreadcrumb({ category: 'Saved Cart', message, data });

/**
 * Copy the cart to localStorage. Returns whether the copy was written — false
 * when localStorage is unusable (blocked, full, or replaced by the in-memory fallback, which would
 * not outlive the tab either).
 */
export function saveCartForLater(basket: IBasketState): boolean {
  if (isStorageFallbackActive('localStorage')) {
    breadcrumb('Not saved: localStorage unavailable');
    return false;
  }

  const saved: SavedCart = {
    savedAt: Date.now(),
    basket: {
      ...basket,
      // The basket slice's own empty values (redux/basket/reducer.ts `initialState`).
      checkoutBasket: null,
      checkoutOrder: null,
      orderSubmitting: false,
      orderCompleted: false,
      loading: false,
      // Transient validate flags the session basket never carries (basket reducer keeps them
      // out of sessionStorage, TEAM-12968 / TEAM-13195). `HYDRATE_BASKET` reads the restored copy
      // back wholesale, so a stale flag would re-show the unavailable-items notice and disable
      // the totals Retry in the new tab until a validate cleared it.
      unavailableBasketItemIds: [],
      unavailableItemsErrorCode: null,
    },
  };

  try {
    localStorage.setItem(LOCAL_STORAGE_KEYS.SAVED_CART, JSON.stringify(saved));
    return true;
  } catch (error) {
    breadcrumb('Not saved: localStorage write failed', { error: String(error) });
    return false;
  }
}

export function clearSavedCart(): void {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SAVED_CART);
  } catch {
    // Nothing was saved if storage cannot be reached.
  }
}

/**
 * The saved copy if it can still be restored, or null when it is unreadable or older than
 * {@link SAVED_CART_TTL_MS}.
 */
function readRestorableCart(raw: string): SavedCart | null {
  let saved: SavedCart;
  try {
    saved = JSON.parse(raw) as SavedCart;
  } catch {
    breadcrumb('Discarded: unreadable');
    return null;
  }

  if (!saved?.basket || typeof saved.savedAt !== 'number') {
    breadcrumb('Discarded: unexpected shape');
    return null;
  }

  if (Date.now() - saved.savedAt >= SAVED_CART_TTL_MS) {
    breadcrumb('Discarded: expired', { savedAt: new Date(saved.savedAt).toISOString() });
    return null;
  }

  return saved;
}

/**
 * Seed this tab's session basket from a saved cart, once. An unreadable or expired copy is deleted
 * first, whether or not the tab has a cart of its own. A copy that is still fresh is restored and
 * deleted only in a tab that has never held a cart; otherwise it is kept for one that has not.
 * "Never held" rather than "holds none": `DESTROY_BASKET` removes `koalaOrder` but not the
 * `koalaSessionId` the first basket write stamps, so the tab that saved the cart — or any tab whose
 * diner emptied or restarted their order — does not get it back on its next route change.
 * Returns whether a cart was restored.
 *
 * Must run before `initializeSessionStorageBasket`, which is what hydrates Redux from the session
 * basket written here.
 */
export function restoreSavedCartIntoSession(): boolean {
  let raw: string | null;
  try {
    raw = localStorage.getItem(LOCAL_STORAGE_KEYS.SAVED_CART);
  } catch {
    return false;
  }
  if (!raw) return false;

  const saved = readRestorableCart(raw);
  if (!saved) {
    clearSavedCart();
    return false;
  }

  try {
    if (
      sessionStorage.getItem(SESSION_STORAGE_KEYS.KOALA_ORDER) ||
      sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID)
    ) {
      return false;
    }
  } catch {
    return false;
  }

  // Write before deleting the copy: a failed write (quota, blocked storage) keeps the saved cart
  // for a later load instead of losing it, and must not throw out of `_app`'s hydrate effect.
  try {
    sessionStorageBasket.set(saved.basket);
  } catch (error) {
    breadcrumb('Not restored: sessionStorage write failed', { error: String(error) });
    return false;
  }
  clearSavedCart();
  breadcrumb('Restored', { itemCount: saved.basket.content?.basket_items?.length ?? 0 });
  return true;
}
